import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { validateEmail } from '../lib/validation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { setAdvancedMatching } from '../utils/metaPixel';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', rememberMe: false });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(form.email);
    if (emailErr) { setError(emailErr); return; }
    setLoading(true); setError('');
    try {
      const result = await login(form.email, form.password, form.rememberMe);
      if (result.requires2fa) {
        navigate('/verify-2fa', { state: { email: result.email } });
      } else {
        // Enable Advanced Matching so all subsequent pixel events include email
        setAdvancedMatching(form.email);
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('[Login] Caught error:', err);
      console.error('[Login] err.message:', err?.message);
      console.error('[Login] err.response:', err?.response?.status, err?.response?.data);
      console.error('[Login] err.stack:', err?.stack);
      setError(err.response?.data?.detail || err?.message || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#f0fafa' }}>
      <div className="w-full max-w-md">

        {/* Logo + brand header */}
        <div className="text-center mb-8">
          <img src="/stratalogy-logo-transparent.png" alt="Stratalogy" className="w-44 h-auto mx-auto mb-5" />
          <h2 className="text-2xl font-bold mb-1" style={{ color: '#1f425c' }}>Welcome back</h2>
          <p className="text-slate-500 text-sm">Sign in to your Stratalogy account</p>
        </div>

        <Card className="bg-white border border-slate-200 shadow-lg">
          <CardContent className="px-6 py-6">
            <form onSubmit={handle} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-slate-700 text-sm font-medium">Email</Label>
                <Input
                  type="email" required placeholder="you@example.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  className="border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#359b95] focus:ring-[#359b95]/20"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-700 text-sm font-medium">Password</Label>
                  <Link to="/forgot-password" className="text-sm hover:underline" style={{ color: '#359b95' }}>Forgot password?</Link>
                </div>
                <div className="relative">
                  <Input
                    type={show ? 'text' : 'password'} required placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    className="border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#359b95] pr-10"
                  />
                  <button type="button" onClick={() => setShow(!show)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="remember" checked={form.rememberMe}
                  onCheckedChange={v => setForm({ ...form, rememberMe: v })}
                  className="border-slate-300 data-[state=checked]:bg-[#359b95] data-[state=checked]:border-[#359b95]" />
                <Label htmlFor="remember" className="text-slate-600 text-sm cursor-pointer">Remember me for 30 days</Label>
              </div>
              <Button type="submit" loading={loading} loadingText="Signing in..."
                className="w-full text-white font-semibold py-2.5 transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)' }}>
                Sign In
              </Button>
            </form>
            <p className="text-center text-slate-500 text-sm mt-5">
              Don't have an account?{' '}
              <Link to="/register" className="font-semibold hover:underline" style={{ color: '#359b95' }}>Create account</Link>
            </p>
            <div className="flex justify-center gap-4 mt-5 pt-4 border-t border-slate-100">
              <Link to="/privacy" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Terms of Service</Link>
              <Link to="/refund-policy" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Refund Policy</Link>
              <Link to="/help" className="text-xs text-slate-400 hover:text-slate-600 transition-colors">Help</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
